import appConfig from '../../../defs/appConfig';
import {
  blockOptionFromCountSize,
  blockOptionToCountSize,
} from '../../../game/util/blockOption';

import { BasicScreen } from '../../../lib/ui/BasicScreen';
import { NumberPicker } from '../../../lib/ui/NumberPicker';
import { Container, Sprite, Text } from '../../../odie';

export type CountSizePopupOptions = {
  onOk?: (option: number) => void;
  option: number;
  width?: number;
  height?: number;
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
};

export class CountSizePopup extends BasicScreen {
  private _onOk?: (option: number) => void;
  protected onClose?: () => void;
  protected title!: Text;
  protected content!: Container;
  protected isClosing = false;
  protected countPicker!: NumberPicker;
  protected widthPicker!: NumberPicker;
  protected heightPicker!: NumberPicker;
  protected pickers: NumberPicker[] = [];

  public async prepare(options: CountSizePopupOptions) {
    const contentWidth = 500;
    const contentHeight = 500;

    this.root.width = appConfig.size.width;
    this.root.height = appConfig.size.height;
    this._spawnScreen();
    this._spawnContent({
      ...options,
      width: contentWidth,
      height: contentHeight,
    });
    this._spawnBackground({
      ...options,
      width: contentWidth,
      height: contentHeight,
    });

    this._onOk = options.onOk;

    const countSize = blockOptionToCountSize(options.option) ?? {
      count: 1,
      width: 1,
      height: 1,
    };
    this.countPicker = this._spawnPicker('Count', countSize.count);
    this.widthPicker = this._spawnPicker(
      'Width',
      countSize.width,
      options.minWidth,
      options.maxWidth,
    );
    this.heightPicker = this._spawnPicker(
      'Height',
      countSize.height,
      options.minHeight,
      options.maxHeight,
    );

    this.title = new Text('Block Count & Size', {
      font: 'Title',
      fontSize: 30,
      align: 'center',
    });
    this.title.x = contentWidth * 0.5;
    this.title.y = 30;
    this.content.addChild(this.title);
  }

  private _spawnPicker(label: string, value: number, min = 1, max = 99) {
    const picker = new NumberPicker({
      value,
      min,
      max,
      width: 220,
      height: 70,
    });
    this.content.addChild(picker);
    picker.x = this.content.width * 0.5 - picker.width * 0.5;
    picker.y = 120 + this.pickers.length * 120;
    this.pickers.push(picker);

    const text = new Text(label, {
      font: 'Title',
      fontSize: 20,
      align: 'center',
    });
    picker.addChild(text);
    text.x = picker.width * 0.5;
    text.y = -32;

    return picker;
  }

  private _spawnScreen() {
    // create translucent screen
    const screen = new Sprite();
    screen.width = this.root.width;
    screen.height = this.root.height;
    screen.view.updateOpts({ backgroundColor: 'rgba(0, 0, 0, 0.8)' });
    this.root.addChild(screen);

    // tapping the screen triggers close request
    screen.setOnTap(() => this.close());
    screen.interactive = true;
  }

  private _spawnContent(options: CountSizePopupOptions) {
    // spawn center pivoted for animation reasons
    const content = (this.content = new Container());
    content.width = options.width ?? 600;
    content.height = options.height ?? 600;
    content.x = this.root.width * 0.5 - content.width * 0.5;
    content.y = this.root.height * 0.5 - content.height * 0.5;
    this.root.addChild(content);
  }

  protected _spawnBackground(options: CountSizePopupOptions) {
    const bg = new Container();

    bg.width = options.width ?? 600;
    bg.height = options.height ?? 600;
    bg.view.updateOpts({
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
    });

    // this blocks input from popup
    bg.interactive = true;
    this.content.addChild(bg);
  }

  public async show() {
    this.root.visible = true;
  }

  public close() {
    this.view.view.removeFromSuperview();
    const option = blockOptionFromCountSize({
      count: this.countPicker.value,
      width: this.widthPicker.value,
      height: this.heightPicker.value,
    });
    this._onOk?.(option);
  }
}
